import React, { useMemo } from "react";
import { Alert, Box, Typography, Stack, Chip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  RepeatOutlined as RepeatOutlinedIcon,
} from "@mui/icons-material";
import {
  isScheduledNotice,
  INTERNAL_TEAM_ONLY_LABEL,
} from "../../utils/noticeScheduling";
import moment from "moment";
import { useAuth } from "../../context/AuthContext";
import { useSubcircles } from "../../hooks/useSubcircles";
import { parseSubcircleName } from "../../utils/subcircleUtils";
import type { Notice } from "../../services/api/types";
import {
  getNoticeEventBounds,
  isCalendarEventPast,
  type NoticeCalendarEventKind,
} from "../../utils/noticeCalendarEvents";
import { lightTheme } from "../../styles/theme";
import {
  getEventRecurrenceDisplay,
  resolveNoticeEventRecurrence,
} from "../../utils/eventRecurrenceDisplay";
import NoticeBodyHtml from "../notices/NoticeBodyHtml";

interface EventDetailsModalProps {
  event: {
    start?: Date;
    notice?: Notice;
    fullTitle?: string;
    kind?: NoticeCalendarEventKind;
  } | null;
  allNotices?: Notice[];
}

function formatNoticeTypeLabel(type?: string): string {
  switch (type) {
    case "info":
      return "Info";
    case "urgent":
      return "Urgent";
    case "event":
    case "custom_event":
      return "Event";
    case "poll":
      return "Poll";
    case "image":
    case "image_notice":
      return "Image";
    case "direct":
      return "Direct";
    default:
      return type
        ? type.charAt(0).toUpperCase() + type.slice(1)
        : "Notice";
  }
}

function getReadableSenderName(
  sender: string | undefined,
  user: ReturnType<typeof useAuth>["user"]
): string {
  if (!sender) return "Noticeboard Admin";
  if (
    sender.match(
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
    )
  ) {
    return "Noticeboard Admin";
  }
  return sender;
}

type InfoColumnProps = {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  primary: string;
  secondary?: string;
  showDivider?: boolean;
};

const InfoColumn: React.FC<InfoColumnProps> = ({
  icon,
  iconBg,
  label,
  primary,
  secondary,
  showDivider = true,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: { xs: 0, sm: 2 },
      py: { xs: 1.25, sm: 0 },
      borderRight: {
        xs: "none",
        sm: showDivider ? "1px solid" : "none",
      },
      borderColor: "divider",
      "&:first-of-type": { pl: { sm: 0 } },
      "&:last-of-type": { pr: { sm: 0 }, borderRight: "none" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: iconBg,
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.35, fontWeight: 500 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.35, fontWeight: 400 }}>
          {primary}
        </Typography>
        {secondary ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.35, mt: 0.15 }}
          >
            {secondary}
          </Typography>
        ) : null}
      </Box>
    </Box>
  </Box>
);

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  event,
  allNotices,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { subcircles } = useSubcircles();

  const notice = event?.notice;
  const kind = event?.kind;
  const title = event?.fullTitle || notice?.title || "Notice";

  const audienceName = useMemo(() => {
    const targetSubcircle = notice?.targetSubcircle;
    if (!targetSubcircle) {
      return user?.schoolName || "Everyone";
    }
    if (targetSubcircle === user?.schoolId) {
      return user?.schoolName || "Everyone";
    }
    const subcircle = subcircles?.find((sc) => sc.circleId === targetSubcircle);
    if (subcircle?.name) return subcircle.name;
    return parseSubcircleName(targetSubcircle);
  }, [notice?.targetSubcircle, subcircles, user?.schoolId, user?.schoolName]);

  const isScheduled = notice ? isScheduledNotice(notice) : false;
  const isPublishLayer = kind === "publish";

  const dateTimeLabel = useMemo(() => {
    if (isPublishLayer && notice?.publishAt) {
      return moment(notice.publishAt).format("dddd, D MMMM YYYY, h:mm A");
    }
    if (event?.start) {
      const datePart = moment(event.start).format("dddd, D MMMM YYYY");
      if (notice?.eventStartTime?.trim()) {
        const combined = moment(
          `${moment(event.start).format("YYYY-MM-DD")}T${notice.eventStartTime.trim()}`
        );
        if (combined.isValid()) {
          return combined.format("dddd, D MMMM YYYY, h:mm A");
        }
      }
      return datePart;
    }
    return "—";
  }, [event?.start, isPublishLayer, notice?.eventStartTime, notice?.publishAt]);

  const createdLabel = notice?.createdAt
    ? moment(notice.createdAt).format("D MMMM YYYY, h:mm A")
    : "—";

  const senderName = getReadableSenderName(notice?.sender, user);

  const isEventCompleted = useMemo(() => {
    if (isPublishLayer || !notice) return false;
    const bounds = getNoticeEventBounds(notice);
    return bounds ? isCalendarEventPast(bounds.end) : false;
  }, [isPublishLayer, notice]);

  const recurrenceDisplay = useMemo(() => {
    if (!notice || isPublishLayer) return null;
    const recurrence = resolveNoticeEventRecurrence(notice, allNotices);
    return getEventRecurrenceDisplay(recurrence, notice);
  }, [allNotices, isPublishLayer, notice]);

  if (!event) return null;

  return (
    <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 }, pt: 0 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.2rem", sm: "1.35rem" },
          lineHeight: 1.25,
          pr: 5,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
        {isScheduled && (
          <Chip
            size="small"
            icon={<ScheduleIcon sx={{ fontSize: "16px !important" }} />}
            label="Scheduled"
            sx={{
              bgcolor: lightTheme.colors.green,
              color: "common.white",
              fontWeight: 600,
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        )}
        {notice?.staffOnly && (
          <Chip
            size="small"
            label={INTERNAL_TEAM_ONLY_LABEL}
            sx={{
              bgcolor: "#f5f0ff",
              color: lightTheme.colors.purple,
              border: "1px solid",
              borderColor: alpha(lightTheme.colors.purple, 0.35),
              fontWeight: 600,
            }}
          />
        )}
        <Chip
          size="small"
          label={formatNoticeTypeLabel(notice?.type)}
          sx={{
            bgcolor: "primary.main",
            color: "common.white",
            fontWeight: 600,
          }}
        />
        <Chip
          size="small"
          icon={<GroupsIcon sx={{ fontSize: "16px !important" }} />}
          label={audienceName}
          variant="outlined"
          sx={{
            borderColor: alpha(lightTheme.colors.purple, 0.35),
            color: lightTheme.colors.purple,
            bgcolor: alpha(lightTheme.colors.purple, 0.06),
            fontWeight: 600,
            "& .MuiChip-icon": { color: lightTheme.colors.purple },
          }}
        />
      </Stack>

      {isEventCompleted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This event has been completed.
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 0, sm: 0 },
          py: { xs: 0, sm: 1 },
          mb: 2,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <InfoColumn
          icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
          iconBg={alpha(theme.palette.primary.main, 0.1)}
          label={isPublishLayer ? "Send Date & Time" : "Event Date & Time"}
          primary={dateTimeLabel}
        />
        <InfoColumn
          icon={<PersonIcon sx={{ fontSize: 18 }} />}
          iconBg={alpha(theme.palette.primary.main, 0.1)}
          label="Sender / Circle"
          primary={senderName}
          secondary={audienceName}
        />
        <InfoColumn
          icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
          iconBg={alpha(theme.palette.primary.main, 0.1)}
          label="Created On"
          primary={createdLabel}
          showDivider={false}
        />
      </Box>

      {recurrenceDisplay && kind !== "publish" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            mb: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <RepeatOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Repeats
            </Typography>
            <Typography variant="body2">
              {recurrenceDisplay.frequencyLabel}
              {recurrenceDisplay.endsLabel
                ? ` until ${recurrenceDisplay.endsLabel}`
                : ""}
            </Typography>
            {recurrenceDisplay.occurrenceLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {recurrenceDisplay.occurrenceLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
      )}

      {notice?.eventLocation && kind !== "publish" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            mb: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.secondary.main, 0.12),
              color: "secondary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LocationOnIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Location
            </Typography>
            <Typography variant="body2">
              {notice.eventLocation}
            </Typography>
          </Box>
        </Box>
      )}

      {notice?.body && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 1 }}
          >
            Notice Content
          </Typography>
          <Box
            sx={{
              lineHeight: 1.65,
              color: "text.primary",
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.12),
            }}
          >
            <NoticeBodyHtml body={notice.body} typographyVariant="body2" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EventDetailsModal;
